import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Renderer2,
  TemplateRef,
  Type,
  afterRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  HoverEventEmitter,
  TooltipConfiguration,
  TooltipTemplateContext,
  isTemplateTooltip,
} from '@simlab/simlab-facility-management/scene-object';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  filter,
  forkJoin,
  map,
  merge,
  of,
  startWith,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { Vector3 } from 'three';
import { TAG_TOOLTIP_DATA, TagTooltipData } from './tag-tooltip-data';

export type TagTooltipWorldToScreen = (
  point: Vector3
) => Observable<Vector3 | undefined>;

export type TagTooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

const ARROW_HEIGHT = 8;
const ARROW_WIDTH = 16;
/** Keeps the arrow away from the rounded corners of the card. */
const ARROW_EDGE_INSET = 12;

type TooltipView = {
  id: string;
  x: number;
  y: number;
  /** Marker radius on screen (px), when the hover event carried a world radius. */
  screenRadius?: number;
  template?: TemplateRef<TooltipTemplateContext>;
  context?: TooltipTemplateContext;
  component?: Type<unknown>;
  inputs?: Record<string, unknown>;
  injector?: Injector;
};

/**
 * Renders the tooltip of the currently hovered tag (at most one at a time) as DOM
 * content positioned over the viewer.
 *
 * Place it inside a `position: relative` element that covers the viewer (the same
 * wrapper the iframe lives in) and feed it:
 * - `hovered`: value of `ICustomComponent.componentHovered$` (Navvis or Matterport),
 * - `worldToScreen`: projection to pixels **relative to that wrapper**
 *   (e.g. `(p) => manager.worldToScreen(p, { w: rect.width, h: rect.height })`),
 * - `reposition` (optional): camera pose stream; on every emission the tooltip is
 *   re-projected, or hidden when `hideOnCameraMove` is set.
 *
 * Placement: the card is centered on the marker and shown on the side with enough room
 * (top, bottom, right, left in that order of preference), `gap` pixels away from the
 * marker edge (`anchorSize` is the marker diameter on screen), with an arrow pointing
 * at the marker. The arrow color follows `--simlab-tag-tooltip-arrow-color` (white by default).
 *
 * Pointer handling: when the pointer leaves the tag the tooltip stays for `closeDelay`
 * milliseconds; entering the card cancels closing, leaving the card closes it.
 * This is required because the card lives in the parent document above the iframe,
 * so hovering it makes the viewer report "unhover".
 *
 * @example
 * <div class="viewer-wrapper" style="position: relative">
 *   <matterport-iframe />
 *   <simlab-tag-tooltip-host
 *     [hovered]="tags.componentHovered$ | async"
 *     [worldToScreen]="worldToScreen"
 *     [reposition]="cameraPoseChange$"
 *   />
 * </div>
 */
@Component({
  selector: 'simlab-tag-tooltip-host',
  standalone: true,
  imports: [NgTemplateOutlet, NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'simlab-tag-tooltip-host' },
  template: `
    @if (view(); as v) {
      <div
        #card
        class="simlab-tag-tooltip"
        [attr.data-tag-id]="v.id"
        (mouseenter)="onCardEnter()"
        (mouseleave)="onCardLeave()"
      >
        <span #arrow class="simlab-tag-tooltip__arrow" aria-hidden="true"></span>
        @if (v.template) {
          <ng-container
            *ngTemplateOutlet="v.template; context: v.context"
          ></ng-container>
        } @else if (v.component) {
          <ng-container
            *ngComponentOutlet="
              v.component;
              inputs: v.inputs;
              injector: v.injector
            "
          ></ng-container>
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        position: absolute;
        inset: 0;
        overflow: hidden;
        pointer-events: none;
        z-index: var(--simlab-tag-tooltip-z-index, 20);
      }
      .simlab-tag-tooltip {
        position: absolute;
        left: 0;
        top: 0;
        /* positioned with transform (see _place): an absolute box with a large \`left\` would
           shrink-to-fit the remaining width near the right edge of the host */
        will-change: transform;
        /* hidden until the first placement pass has run (see _place) */
        visibility: hidden;
        pointer-events: auto;
        width: max-content;
        max-width: var(--simlab-tag-tooltip-max-width, min(420px, 90%));
      }
      .simlab-tag-tooltip__arrow {
        position: absolute;
        width: 0;
        height: 0;
        border: 8px solid transparent;
        filter: drop-shadow(0 0 8px rgba(13, 14, 18, 0.18));
        pointer-events: none;
      }
      .simlab-tag-tooltip[data-placement='top'] .simlab-tag-tooltip__arrow {
        bottom: -16px;
        border-top-color: var(--simlab-tag-tooltip-arrow-color, #fff);
      }
      .simlab-tag-tooltip[data-placement='bottom'] .simlab-tag-tooltip__arrow {
        top: -16px;
        border-bottom-color: var(--simlab-tag-tooltip-arrow-color, #fff);
      }
      .simlab-tag-tooltip[data-placement='left'] .simlab-tag-tooltip__arrow {
        right: -16px;
        border-left-color: var(--simlab-tag-tooltip-arrow-color, #fff);
      }
      .simlab-tag-tooltip[data-placement='right'] .simlab-tag-tooltip__arrow {
        left: -16px;
        border-right-color: var(--simlab-tag-tooltip-arrow-color, #fff);
      }
    `,
  ],
})
export class TagTooltipHostComponent {
  private readonly _renderer = inject(Renderer2);
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _injector = inject(Injector);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _card = viewChild<ElementRef<HTMLElement>>('card');
  private readonly _arrow = viewChild<ElementRef<HTMLElement>>('arrow');

  /** Currently hovered component from `ICustomComponent.componentHovered$`. */
  readonly hovered = input<HoverEventEmitter | null | undefined>(undefined);
  /** Projects a world point to pixels relative to this host's parent. */
  readonly worldToScreen = input.required<TagTooltipWorldToScreen>();
  /** Camera pose / viewport change stream; triggers re-projection (or hiding). */
  readonly reposition = input<Observable<unknown> | null | undefined>(
    undefined
  );
  /** Hide the tooltip as soon as the camera moves instead of following the tag. */
  readonly hideOnCameraMove = input<boolean>(false);
  /** Grace period (ms) between leaving the tag and closing the tooltip. */
  readonly closeDelay = input<number>(200);
  /** Distance (px) between the marker edge and the arrow tip. */
  readonly gap = input<number>(4);
  /** Marker diameter on screen (px); the tooltip is placed outside this circle around the anchor point. */
  readonly anchorSize = input<number>(0);
  /** Preferred sides, tried in order; the first with enough room wins, otherwise the roomiest. */
  readonly placements = input<TagTooltipPlacement[]>(['top', 'bottom', 'right', 'left']);

  readonly opened = output<string>();
  readonly closed = output<void>();

  private readonly _cardHovered = new BehaviorSubject<boolean>(false);
  private readonly _view = signal<TooltipView | null>(null);
  readonly view = computed(() => this._view());

  constructor() {
    const hovered$ = toObservable(this.hovered);
    const reposition$ = toObservable(this.reposition).pipe(
      switchMap((source) => source ?? EMPTY)
    );
    const closeDelay$ = toObservable(this.closeDelay);

    const closeNow$ = reposition$.pipe(
      filter(() => this.hideOnCameraMove()),
      map(() => undefined)
    );

    // Active tooltip: keeps the last hovered value during the grace period and
    // while the pointer is over the card itself.
    const active$: Observable<HoverEventEmitter | undefined> = merge(
      hovered$.pipe(map((value) => value ?? undefined)),
      closeNow$
    ).pipe(
      switchMap((value) => {
        if (value) return of(value);
        return closeDelay$.pipe(
          take(1),
          switchMap((delay) => timer(delay)),
          switchMap(() =>
            this._cardHovered.pipe(
              filter((over) => !over),
              take(1)
            )
          ),
          map(() => undefined)
        );
      })
    );

    combineLatest([active$, reposition$.pipe(startWith(null))])
      .pipe(
        switchMap(([active]) =>
          active
            ? this._project(active).pipe(
                map(({ screen, screenRadius }) => ({ active, screen, screenRadius }))
              )
            : of({ active: undefined, screen: undefined, screenRadius: undefined })
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(({ active, screen, screenRadius }) =>
        this._apply(active, screen, screenRadius)
      );

    afterRender(() => this._place());
  }

  onCardEnter() {
    this._cardHovered.next(true);
  }

  onCardLeave() {
    this._cardHovered.next(false);
  }

  /**
   * Projects the anchor and, when the event carries a world radius, a second point one
   * radius above it; their pixel distance is the marker radius on screen.
   */
  private _project(
    active: HoverEventEmitter
  ): Observable<{ screen: Vector3 | undefined; screenRadius: number | undefined }> {
    const project = this.worldToScreen();
    const center$ = project(active.point).pipe(take(1));
    if (!active.radius) {
      return center$.pipe(map((screen) => ({ screen, screenRadius: undefined })));
    }
    const edge = active.point.clone().add(new Vector3(0, active.radius, 0));
    return forkJoin([center$, project(edge).pipe(take(1))]).pipe(
      map(([screen, edgeScreen]) => ({
        screen,
        screenRadius:
          screen && edgeScreen
            ? Math.hypot(edgeScreen.x - screen.x, edgeScreen.y - screen.y)
            : undefined,
      }))
    );
  }

  private _apply(
    active: HoverEventEmitter | undefined,
    screen: Vector3 | undefined,
    screenRadius: number | undefined
  ) {
    const previous = this._view();
    // z > 1 means the point is behind the camera (three.js / Matterport convention)
    if (!active || !active.tooltip || !screen || screen.z > 1) {
      this._view.set(null);
      if (previous) this.closed.emit();
      return;
    }
    // Same tag as before (e.g. a camera move re-projecting the anchor): keep the rendered content.
    // A fresh injector / context would make ngComponentOutlet destroy and re-create the tooltip
    // component, losing its state and any tap in flight on it.
    const content =
      previous?.id === active.id
        ? previous
        : this._content(active, active.tooltip);
    const next: TooltipView = {
      id: active.id,
      x: screen.x,
      y: screen.y,
      screenRadius,
      template: content.template,
      context: content.context,
      component: content.component,
      inputs: content.inputs,
      injector: content.injector,
    };
    this._view.set(next);
    if (previous?.id !== active.id) this.opened.emit(active.id);
  }

  private _content(
    hovered: HoverEventEmitter,
    tooltip: TooltipConfiguration
  ): Partial<TooltipView> {
    const data: TagTooltipData = { id: hovered.id, userData: hovered.userData };
    if (isTemplateTooltip(tooltip)) {
      return {
        template: tooltip.template,
        context: {
          ...(tooltip.context ?? {}),
          $implicit: hovered.userData,
          id: hovered.id,
          userData: hovered.userData,
        },
      };
    }
    return {
      component: tooltip.component,
      inputs: tooltip.inputs,
      injector: Injector.create({
        parent: this._injector,
        providers: [{ provide: TAG_TOOLTIP_DATA, useValue: data }],
      }),
    };
  }

  /**
   * Positions the card on one of the four sides of the marker, centered on it and clamped
   * to the host, and points the arrow at the marker. Runs after render, so it only touches
   * the DOM and never writes signals.
   */
  private _place() {
    const card = this._card()?.nativeElement;
    const arrow = this._arrow()?.nativeElement;
    const view = this._view();
    if (!card || !arrow || !view) return;
    const host = this._host.nativeElement.getBoundingClientRect();
    if (host.width === 0 || host.height === 0) return;

    const width = card.offsetWidth;
    const height = card.offsetHeight;
    const anchorRadius = Math.max(this.anchorSize() / 2, view.screenRadius ?? 0);
    const offset = anchorRadius + this.gap() + ARROW_HEIGHT;
    const { x, y } = view;

    const room: Record<TagTooltipPlacement, number> = {
      top: y - offset - height,
      bottom: host.height - (y + offset + height),
      right: host.width - (x + offset + width),
      left: x - offset - width,
    };
    const order = this.placements();
    const placement =
      order.find((side) => room[side] >= 0) ??
      order.reduce((best, side) => (room[side] > room[best] ? side : best), order[0]);

    let left: number;
    let top: number;
    if (placement === 'top' || placement === 'bottom') {
      left = Math.max(0, Math.min(x - width / 2, host.width - width));
      top = placement === 'top' ? y - offset - height : y + offset;
      const arrowLeft = Math.max(
        ARROW_EDGE_INSET,
        Math.min(x - left - ARROW_WIDTH / 2, width - ARROW_EDGE_INSET - ARROW_WIDTH)
      );
      this._renderer.setStyle(arrow, 'left', `${arrowLeft}px`);
      this._renderer.removeStyle(arrow, 'top');
    } else {
      top = Math.max(0, Math.min(y - height / 2, host.height - height));
      left = placement === 'right' ? x + offset : x - offset - width;
      const arrowTop = Math.max(
        ARROW_EDGE_INSET,
        Math.min(y - top - ARROW_WIDTH / 2, height - ARROW_EDGE_INSET - ARROW_WIDTH)
      );
      this._renderer.setStyle(arrow, 'top', `${arrowTop}px`);
      this._renderer.removeStyle(arrow, 'left');
    }

    this._renderer.setAttribute(card, 'data-placement', placement);
    this._renderer.setStyle(
      card,
      'transform',
      `translate(${Math.round(left)}px, ${Math.round(top)}px)`
    );
    this._renderer.setStyle(card, 'visibility', 'visible');
  }
}
